import React, { useState } from "react";
import { Link } from "react-router-dom";
import { photoUrl, INR } from "@/lib/api";
import { MapPin, BedDouble, Zap, ShieldCheck, Users, Home } from "lucide-react";

export default function ListingCard({ listing }) {
  const [imgError, setImgError] = useState(false);
  const cover = listing.photos?.[0];

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="listing-card h-full flex flex-col bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow duration-200"
    >
      {/* Fixed aspect ratio 4:3 photo container */}
      <div className="relative aspect-[4/3] w-full bg-stone-100 flex items-center justify-center overflow-hidden shrink-0">
        {cover && !imgError ? (
          <img
            loading="lazy"
            src={photoUrl(cover)}
            alt={listing.title}
            onError={() => setImgError(true)}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-stone-400 gap-1 select-none">
            <Home className="w-8 h-8 opacity-40" />
            <span className="text-[11px] font-medium text-stone-400">No image</span>
          </div>
        )}

        {listing.boosted && (
          <div className="absolute top-3 left-3 chip bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1">
            <Zap className="w-3 h-3" /> Boosted
          </div>
        )}
        {listing.owner_verified && (
          <div className="absolute top-3 right-3 chip chip-verified">
            <ShieldCheck className="w-3 h-3" /> Verified
          </div>
        )}
        <div className="absolute bottom-3 left-3 chip bg-white/95 text-stone-800 border border-stone-200">
          {listing.listing_category === "Flatmate Needed" ? (
            <><Users className="w-3 h-3" /> Flatmate</>
          ) : (
            <><BedDouble className="w-3 h-3" /> {listing.bhk}</>
          )}
        </div>
      </div>

      {/* Fixed height content area so cards in grid rows align uniformly */}
      <div className="p-4 h-[142px] flex flex-col justify-between flex-1">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display font-medium text-stone-900 leading-tight line-clamp-1 flex-1">
              {listing.title}
            </h3>
            <div className="text-right shrink-0">
              <div className="font-display font-semibold text-brand text-lg leading-none">{INR(listing.rent)}</div>
              <div className="text-[9px] uppercase tracking-widest text-stone-500 mt-1">/month</div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-sm text-stone-600">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{listing.locality ? `${listing.locality}, ` : ""}{listing.city}</span>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-1 mt-auto pt-2 overflow-hidden h-[26px]">
          {listing.furnishing && <span className="chip text-[11px] py-0.5 px-2 capitalize">{listing.furnishing}</span>}
          {listing.room_type && <span className="chip text-[11px] py-0.5 px-2 capitalize">{listing.room_type}</span>}
          {listing.preferences?.gender && listing.preferences.gender !== "any" && (
            <span className="chip text-[11px] py-0.5 px-2 capitalize">{listing.preferences.gender} pref</span>
          )}
          {listing.preferences?.food_pref === "veg" && <span className="chip text-[11px] py-0.5 px-2">Veg</span>}
        </div>
      </div>
    </Link>
  );
}
